/**
 * handleApiError.ts
 * -----------------
 * Orchestrator the axios interceptor calls first for every post-login request.
 * It parses the wrapped envelope, decides the action via the pure policy, and
 * for terminal UI cases (toasts / 400) shows the UI here and returns 'HANDLED'.
 * For flow-control cases it returns a small enum the interceptor acts on.
 *
 * Returns 'PASS' for anything that is not a wrapped-contract error, so the
 * interceptor's existing behavior runs unchanged.
 */
import { parseApiError } from './errorContract';
import { decideAuthAction } from './authErrorPolicy';
import { showServiceUnavailableToast, showPlatformErrorToast } from './authErrorPresenter';

export type HandleResult =
  | 'REFRESH' //        caller: run refresh + retry original request
  | 'LOGIN_REQUIRED' // caller: force login, DO NOT loop
  | 'RETRY_LATER' //    caller: capped backoff retry
  | 'HANDLED' //        UI already shown -> just reject
  | 'PASS'; //          not an auth-contract error -> existing handling

export function handleApiError(error: any): HandleResult {
  const parsed = parseApiError(error);
  if (!parsed) {
    return 'PASS';
  }

  switch (decideAuthAction(parsed.messageCode, parsed.messageDescription)) {
    case 'REFRESH_TOKEN':
      return 'REFRESH';
    case 'LOGIN_REQUIRED':
      return 'LOGIN_REQUIRED';
    case 'RETRY_LATER':
      return 'RETRY_LATER';
    case 'PLATFORM_ERROR':
      showPlatformErrorToast(parsed.correlationId);
      return 'HANDLED';
    case 'SERVICE_UNAVAILABLE':
      showServiceUnavailableToast(parsed.correlationId);
      return 'HANDLED';
    case 'FIX_REQUEST':
      console.warn('[Auth] 400 - fix request:', parsed.messageDetails);
      return 'HANDLED';
    default:
      return 'PASS';
  }
}
